<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, Select, Text, Toggle } from '@Shelly/shelly-design-system';
	import CameraStreamPreview from './CameraStreamPreview.svelte';
	import { getCameraConfig, saveCameraConfig } from '../../helpers/camera-settings-helpers';
	import { hasCameraConfigKey } from '../../helpers/camera-features';
	import type {
		CameraAntiflicker,
		CameraVideoConfig,
		CameraVideoOrientationState,
	} from '../../types/camera-settings';

	export let device: Device;

	type VideoAdjustmentsState = CameraVideoOrientationState & { antiflicker: CameraAntiflicker };

	const DEFAULT_ANTIFLICKER: CameraAntiflicker = '50Hz';
	const AUTOSAVE_DELAY = 500;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const supportsAntiflicker = hasCameraConfigKey(device, 'video.antiflicker');

	const readVideo = (): VideoAdjustmentsState => {
		const raw = getCameraConfig(device)?.video;
		return {
			flip: typeof raw?.flip === 'boolean' ? raw.flip : false,
			mirror: typeof raw?.mirror === 'boolean' ? raw.mirror : false,
			antiflicker: raw?.antiflicker ?? DEFAULT_ANTIFLICKER,
		};
	};

	let video: VideoAdjustmentsState = readVideo();
	let cache: VideoAdjustmentsState = { ...video };
	let loading = false;
	let saveTimeout: ReturnType<typeof setTimeout> | undefined;

	$: antiflickerOptions = [
		{ title: $format('camera_video_antiflicker_50', { default: '50 Hz' }), value: '50Hz' },
		{ title: $format('camera_video_antiflicker_60', { default: '60 Hz' }), value: '60Hz' },
	];

	$: hasChanges =
		video.flip !== cache.flip ||
		video.mirror !== cache.mirror ||
		video.antiflicker !== cache.antiflicker;

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: resetToDefaults,
				loading,
				disabled: loading,
				label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const getSelectValue = (e: CustomEvent): any => {
		const d = e.detail;
		if (typeof d === 'string' || typeof d === 'number') return d;
		return d?.value ?? d;
	};

	const scheduleSave = () => {
		if (saveTimeout) clearTimeout(saveTimeout);
		saveTimeout = setTimeout(save, AUTOSAVE_DELAY);
	};

	/** Only sends the fields this firmware actually exposes. */
	const buildConfig = (): CameraVideoConfig => {
		const config: CameraVideoConfig = { flip: video.flip, mirror: video.mirror };
		if (supportsAntiflicker) config.antiflicker = video.antiflicker;
		return config;
	};

	const save = async () => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = undefined;
		}
		if (!hasChanges) return;
		loading = true;
		const currentVideo = getCameraConfig(device)?.video ?? {};
		const success = await saveCameraConfig(
			device,
			{ video: { ...currentVideo, ...buildConfig() } },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cache = { ...video };
		} else {
			video = { ...cache };
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		loading = true;
		const currentVideo = getCameraConfig(device)?.video ?? {};
		const resetVideo: CameraVideoConfig = { ...currentVideo, flip: null, mirror: null };
		if (supportsAntiflicker) resetVideo.antiflicker = null;
		const success = await saveCameraConfig(
			device,
			{ video: resetVideo },
			$format('camera_video_reset_success', { default: 'Video settings reset to defaults.' }),
		);
		if (success) {
			video = readVideo();
			cache = { ...video };
		}
		loading = false;
	};

	onDestroy(() => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			save();
		}
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<CameraStreamPreview {device} />
{/if}

<div class="mb-s-plus">
	<Toggle
		size="m"
		checked={video.flip}
		disabled={loading}
		on:click={() => {
			video = { ...video, flip: !video.flip };
			scheduleSave();
		}}
		id="camera-video-flip"
	>
		{$format('camera_video_flip', { default: 'Flip image' })}
	</Toggle>
	<Text styleType="body-extra-small" class="text-color-muted mt-xs d-block">
		{$format('camera_video_flip_hint', { default: 'Rotate the image 180°.' })}
	</Text>
</div>

<div class="mb-s-plus">
	<Toggle
		size="m"
		checked={video.mirror}
		disabled={loading}
		on:click={() => {
			video = { ...video, mirror: !video.mirror };
			scheduleSave();
		}}
		id="camera-video-mirror"
	>
		{$format('camera_video_mirror', { default: 'Mirror image' })}
	</Toggle>
	<Text styleType="body-extra-small" class="text-color-muted mt-xs d-block">
		{$format('camera_video_mirror_hint', { default: 'Flip the image horizontally.' })}
	</Text>
</div>

{#if supportsAntiflicker}
	<div class="mb-s-plus">
		<Text styleType="controls-small" class="mb-xs d-block">
			{$format('camera_video_antiflicker', { default: 'Anti-flicker' })}
		</Text>
		<Select
			size="m"
			width="full"
			icon="fas fa-lightbulb"
			label={$format('camera_video_antiflicker', { default: 'Anti-flicker' })}
			options={antiflickerOptions}
			value={antiflickerOptions.find((o) => o.value === video.antiflicker)}
			on:change={(e) => {
				video = { ...video, antiflicker: getSelectValue(e) };
				scheduleSave();
			}}
			disabled={loading}
			id="camera-video-antiflicker"
			aria-label={$format('camera_video_antiflicker', { default: 'Anti-flicker' })}
		/>
		<Text styleType="body-extra-small" class="text-color-muted mt-xs d-block">
			{$format('camera_video_antiflicker_hint', {
				default: 'Match your mains frequency to stop artificial lighting flickering on video.',
			})}
		</Text>
	</div>
{/if}

{#if !$showNewLayout}
	<Button
		variant="secondary"
		width="full"
		size="m"
		{loading}
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
{/if}
